import { useTranslation } from "react-i18next";
import { useSalesMarkup } from "../../../hooks/useSalesMarkup";

/**
 * "Sales price" chip next to a price block (flag priceMarkupEnabled): tells
 * the dealer that the numbers around it are sales prices (purchase price +
 * markup, or the customer's own commission price), not purchase prices, so
 * nobody reads a marked-up number as "our price went up". Renders nothing
 * while the markup is inactive = byte-identical DOM with the flag off.
 *
 * Layout contract: the chip is a plain `inline` (not inline-flex, so no
 * soft-wrap opportunity before it and no line-box growth from its
 * border/padding), nowrap, 9px with line-height 1 and no vertical margin -
 * it sits on the SAME line as the amount/label it marks and never adds a
 * line or changes the height/width of what it stands next to. Callers add
 * at most a horizontal margin (ml-1 / ml-1.5), never mt/mb/self-start.
 */
export function SalesPriceBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { active } = useSalesMarkup();
  if (!active) return null;
  return (
    <span
      data-testid="sales-price-badge"
      className={`inline align-middle whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-1 py-0 text-[9px] font-semibold uppercase leading-none text-emerald-700 ${
        className ?? ""
      }`}
    >
      {t("salesMarkup.badge")}
    </span>
  );
}
